// 📄 pages/Admin/components/TablaFormModal.tsx
import React, { useState } from 'react';
import { X, Save, Plus, Trash2 } from 'lucide-react';
import {
  TablaEstandar, TablaEstandarData, saveTablaEstandar
} from '../../../services/catalogoTablasService.ts';
import { CampoTabla } from '../../../services/tablaZService.ts';
import { SolucionBadi, SOLUCIONES_BADI } from '../../../services/catalogoBadisService.ts';

interface TablaFormModalProps {
  tabla: TablaEstandar | null; // null = alta nueva
  onClose: () => void;
  onSaved: () => void;
}

const generarId = (): string => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const EMPTY_FORM: TablaEstandarData = { nombreTecnico: '', transaccion: 'SE16N', descripcion: '', solucion: 'general', campos: [] };

export default function TablaFormModal({ tabla, onClose, onSaved }: TablaFormModalProps) {
  const [form, setForm] = useState<TablaEstandarData>(
    tabla
      ? { nombreTecnico: tabla.nombreTecnico, transaccion: tabla.transaccion, descripcion: tabla.descripcion, solucion: tabla.solucion, campos: tabla.campos }
      : EMPTY_FORM
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCampoChange = (index: number, campo: Partial<CampoTabla>) => {
    const campos = [...form.campos];
    campos[index] = { ...campos[index], ...campo };
    setForm({ ...form, campos });
  };
  const addCampo = () => setForm({ ...form, campos: [...form.campos, { nombre: '', descripcion: '' }] });
  const removeCampo = (index: number) => setForm({ ...form, campos: form.campos.filter((_, i) => i !== index) });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.nombreTecnico.trim()) { setError('El nombre técnico de la tabla es obligatorio.'); return; }

    setSaving(true);
    try {
      await saveTablaEstandar(tabla?.id || generarId(), form, !tabla);
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Error al guardar la tabla.');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-xl shadow-2xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="text-lg font-medium text-white">{tabla ? 'Editar Tabla' : 'Nueva Tabla'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

          <div>
            <label className={labelClass}>Nombre técnico de la tabla*</label>
            <input
              type="text" className={`${inputClass} font-mono`} value={form.nombreTecnico}
              onChange={(e) => setForm({ ...form, nombreTecnico: e.target.value })}
              placeholder="ej. ARBCIG_MSG_ATTRS"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Transacción</label>
              <input
                type="text" className={`${inputClass} font-mono`} value={form.transaccion}
                onChange={(e) => setForm({ ...form, transaccion: e.target.value })}
                placeholder="ej. SE16N"
              />
            </div>
            <div>
              <label className={labelClass}>Solución</label>
              <select className={inputClass} value={form.solucion} onChange={(e) => setForm({ ...form, solucion: e.target.value as SolucionBadi })}>
                {SOLUCIONES_BADI.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className={labelClass}>Descripción</label>
            <textarea
              className={`${inputClass} resize-y`} rows={2} value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              placeholder="Para qué se usa esta tabla..."
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={labelClass}>Campos</label>
              <button type="button" onClick={addCampo} className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300">
                <Plus className="w-3.5 h-3.5" /> Agregar campo
              </button>
            </div>
            {form.campos.length === 0 ? (
              <p className="text-xs text-gray-500">Sin campos agregados todavía.</p>
            ) : (
              <div className="space-y-2">
                {form.campos.map((campo, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="text" value={campo.nombre} onChange={(e) => handleCampoChange(i, { nombre: e.target.value })}
                      placeholder="Nombre del campo" className={`${inputClass} font-mono flex-1`}
                    />
                    <input
                      type="text" value={campo.descripcion} onChange={(e) => handleCampoChange(i, { descripcion: e.target.value })}
                      placeholder="Descripción / ejemplo" className={`${inputClass} flex-1`}
                    />
                    <button type="button" onClick={() => removeCampo(i)} className="text-gray-500 hover:text-red-400 flex-shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-600 px-4 py-2 rounded-lg text-sm font-semibold text-[#fff] transition-all"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
